import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  SlidersHorizontal,
} from "lucide-react";
import { StatusMeter, TeamLogo } from "../components/bondhouse/FixtureStatus";
import OddsMarketSection from "../components/operator/OddsMarketSection";
import OperatorConsoleShell from "../components/operator/OperatorConsoleShell";
import { selectAppUser } from "../redux/appAuthSlice";
import {
  clearFixtureOddsFeeds,
  clearOddsAutofillResult,
  clearOddsSaveState,
  fetchFixtureOddsFeeds,
  fetchOddsAutofill,
  saveHouseFixtureOdds,
} from "../redux/oddsCustomizeSlice";
import {
  fetchHouseFixtures,
} from "../redux/operatorConsoleSlice";
import { isBetHousePartnershipReadyForConsole } from "../utils/betHousePartners";
import {
  FIXTURE_TAB,
  fixtureKickoffShort,
  fixtureMatchLabel,
  fixtureStatusPayload,
  fixturesDeskPath,
  resolveFixtureId,
} from "../utils/fixturesDeskHelpers";
import {
  collectValidationErrors,
  countOddsSummary,
  deepMergeOdds,
  formatOddsInput,
  hasAnyFilledOddsInput,
  isMissingOddsFeedMessage,
  listOddsMarketSections,
  ODDS_FIELD_LABELS,
  sectionHasOddsValues,
  updateOddsAtPath,
  validateOddsValue,
} from "../utils/oddsCustomizeHelpers";
import "../betdetail.css";
import "../bettors-desk.css";

const CustomizeOddsDesk = () => {
  const { houseId, fixtureId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userId = useSelector(selectAppUser)?._id;

  const { houses, houseFixtures, houseFixturesHouseId } = useSelector(
    (s) => s.operatorConsole
  );
  const {
    fixtureOddsFeeds,
    fixtureOddsFeedsStatus,
    fixtureOddsFeedsError,
    autofillResult,
    autofillStatus,
    saveStatus,
    saveError,
  } = useSelector((s) => s.oddsCustomize);

  const [customOdds, setCustomOdds] = useState({});
  const [selectedBookmaker, setSelectedBookmaker] = useState(null);
  const [autofilledSections, setAutofilledSections] = useState({});
  const [toast, setToast] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [savedOddsBaseline, setSavedOddsBaseline] = useState(null);

  const fixturesPath = fixturesDeskPath(houseId);
  const fixturesNeedOddsPath = fixturesDeskPath(houseId, FIXTURE_TAB.NEED_ODDS);
  const bet = useMemo(
    () => houses.find((h) => String(h._id) === String(houseId)),
    [houses, houseId]
  );
  const partnershipReady = isBetHousePartnershipReadyForConsole(bet);

  const fixture = useMemo(
    () =>
      houseFixtures.find((f) => String(resolveFixtureId(f)) === String(fixtureId)) || null,
    [houseFixtures, fixtureId]
  );
  const home = fixture?.homeTeam || {};
  const away = fixture?.awayTeam || {};
  const feedState = fixtureId ? fixtureOddsFeeds[fixtureId] : null;
  const bookmakers = feedState?.bookmakers || [];

  useEffect(() => {
    if (!toast) return undefined;
    const id = window.setTimeout(() => setToast(null), 4200);
    return () => window.clearTimeout(id);
  }, [toast]);

  useEffect(() => {
    if (!houseId) return undefined;
    if (houseFixturesHouseId !== houseId) {
      dispatch(fetchHouseFixtures({ betId: houseId }));
    }
    return undefined;
  }, [dispatch, houseId, houseFixturesHouseId]);

  useEffect(() => {
    setInitialized(false);
    setSavedOddsBaseline(null);
  }, [fixtureId]);

  useEffect(() => {
    if (!fixture || initialized) return;
    const baseline = fixture.odds || {};
    setCustomOdds(baseline);
    setSavedOddsBaseline(baseline);
    setInitialized(true);
  }, [fixture, initialized]);

  useEffect(() => {
    if (!fixtureId) return undefined;
    dispatch(fetchFixtureOddsFeeds({ fixtureId }));
    return () => {
      dispatch(clearFixtureOddsFeeds());
      dispatch(clearOddsAutofillResult());
      dispatch(clearOddsSaveState());
    };
  }, [dispatch, fixtureId]);

  useEffect(() => {
    if (!autofillResult?.success || !autofillResult.section || !autofillResult.data) return;
    const { section, data, bookmaker } = autofillResult;
    setCustomOdds((prev) => ({
      ...prev,
      [section]: deepMergeOdds(prev[section] || {}, data),
    }));
    setAutofilledSections((prev) => ({
      ...prev,
      [section]: { bookmaker, timestamp: new Date().toISOString() },
    }));
    setToast({
      type: "success",
      message: `${ODDS_FIELD_LABELS[section] || section} loaded from ${bookmaker?.displayName || "feed"}.`,
    });
    dispatch(clearOddsAutofillResult());
  }, [autofillResult, dispatch]);

  const validationErrors = useMemo(() => collectValidationErrors(customOdds), [customOdds]);
  const validationErrorCount = Object.keys(validationErrors).length;
  const oddsSummary = useMemo(() => countOddsSummary(customOdds), [customOdds]);
  const marketSections = useMemo(() => listOddsMarketSections(customOdds), [customOdds]);
  const feedsLoading = fixtureOddsFeedsStatus === "loading" && !feedState;
  const feedsError =
    fixtureOddsFeedsError && !isMissingOddsFeedMessage(fixtureOddsFeedsError)
      ? fixtureOddsFeedsError
      : null;
  const saving = saveStatus === "loading";
  const autofillLoading = autofillStatus === "loading";
  const hasFilledOdds = hasAnyFilledOddsInput(customOdds);
  const hasUnsavedChanges = useMemo(() => {
    if (!initialized || savedOddsBaseline === null) return false;
    return JSON.stringify(customOdds) !== JSON.stringify(savedOddsBaseline);
  }, [customOdds, savedOddsBaseline, initialized]);
  const canSave =
    hasFilledOdds && hasUnsavedChanges && validationErrorCount === 0 && partnershipReady;
  const saveBtnActive = hasUnsavedChanges && hasFilledOdds;

  const handleChange = useCallback((path, value) => {
    const formatted = formatOddsInput(value);
    setCustomOdds((prev) => updateOddsAtPath(prev, path, () => formatted));
    const section = path.split(".")[0];
    setAutofilledSections((prev) => {
      if (!prev[section]) return prev;
      const next = { ...prev };
      delete next[section];
      return next;
    });
  }, []);

  const handleBlur = useCallback((path) => {
    setCustomOdds((prev) =>
      updateOddsAtPath(prev, path, (value) => {
        const validation = validateOddsValue(value);
        return validation.valid ? validation.normalizedValue : value;
      })
    );
  }, []);

  const handleAutofill = useCallback(
    (sectionTitle) => {
      if (!fixtureId) return;
      if (!selectedBookmaker) {
        setToast({ type: "error", message: "Select a bookmaker from the feed strip first." });
        return;
      }
      const bookmakerData = bookmakers.find((b) => b.id === selectedBookmaker.id);
      const sectionOdds = bookmakerData?.odds?.[sectionTitle];
      if (!sectionOdds || !sectionHasOddsValues(sectionOdds)) {
        setToast({
          type: "error",
          message: `${selectedBookmaker.displayName} has no odds for this market.`,
        });
        return;
      }
      dispatch(
        fetchOddsAutofill({
          fixtureId,
          bookmakerId: selectedBookmaker.id,
          section: sectionTitle,
        })
      );
    },
    [bookmakers, dispatch, fixtureId, selectedBookmaker]
  );

  const handleSave = async () => {
    if (!partnershipReady) {
      setToast({ type: "error", message: "All partners must accept before saving odds." });
      return;
    }
    if (validationErrorCount > 0) {
      setToast({ type: "error", message: "Fix validation errors before saving." });
      return;
    }
    if (!houseId || !fixtureId || !userId) return;

    try {
      await dispatch(
        saveHouseFixtureOdds({
          betId: houseId,
          fixtureId,
          odds: customOdds,
          operatorUserId: userId,
        })
      ).unwrap();
      await dispatch(fetchHouseFixtures({ betId: houseId }));
      setToast({ type: "success", message: "Odds saved successfully." });
      window.setTimeout(() => navigate(fixturesNeedOddsPath), 600);
    } catch (err) {
      setToast({ type: "error", message: String(err) });
    }
  };

  if (!houseId || !fixtureId) {
    return (
      <OperatorConsoleShell activeSection="fixtures" mainClassName="oc-desk-main">
        <div className="bd-page oc-odds-page">
          <div className="bd-loading">Missing house or fixture.</div>
        </div>
      </OperatorConsoleShell>
    );
  }

  return (
    <OperatorConsoleShell activeSection="fixtures" mainClassName="oc-desk-main">
      <div className="bd-page oc-odds-page">
      <header className="fd-operator-header fd-operator-header-odds" aria-label="Customize odds">
        <div className="fd-desk-bar oc-odds-desk-bar">
          <div className="fd-desk-bar-start oc-odds-desk-brand">
            <span className="oc-odds-desk-mark" aria-hidden>
              <SlidersHorizontal size={12} strokeWidth={2.2} />
            </span>
          </div>

          {fixture ? (
            <div className="fd-desk-bar-center">
              <div
                className="oc-odds-desk-fixture"
                aria-label={fixtureMatchLabel(fixture)}
                title={fixtureKickoffShort(fixture)}
              >
                <span className="fd-fixture-team-pair">
                  <TeamLogo team={home} size="sm" />
                  <span className="fd-fixture-team-name">{home.name || "Home"}</span>
                </span>
                <div className="oc-odds-desk-status">
                  <StatusMeter fixture={fixtureStatusPayload(fixture)} variant="compact" />
                </div>
                <span className="fd-fixture-team-pair fd-fixture-team-pair-away">
                  <span className="fd-fixture-team-name">{away.name || "Away"}</span>
                  <TeamLogo team={away} size="sm" />
                </span>
              </div>
            </div>
          ) : null}

          <div className="fd-desk-bar-end fd-desk-actions">
            <button
              type="button"
              className={`oc-odds-save-btn ${saveBtnActive ? "is-ready" : "is-idle"}`}
              disabled={saving || !canSave}
              title={
                !hasUnsavedChanges
                  ? "No changes to save"
                  : !hasFilledOdds
                  ? "Enter at least one odds value to save"
                  : !partnershipReady
                  ? "All partners must accept before saving"
                  : validationErrorCount > 0
                  ? "Fix validation errors before saving"
                  : "Save changes to the house board"
              }
              onClick={handleSave}
            >
              {saving ? (
                <Loader2 size={14} strokeWidth={2.3} className="animate-spin" />
              ) : (
                <CheckCircle2 size={14} strokeWidth={2.3} aria-hidden />
              )}
              <span>Save</span>
            </button>
          </div>
        </div>

        <div className="oc-odds-desk-meta" aria-label="Odds progress">
          <span>
            <em>Fields</em>
            <strong className="tabular-nums">{oddsSummary.markets}</strong>
          </span>
          <span>
            <em>Filled</em>
            <strong className="tabular-nums oc-odds-stat-good">{oddsSummary.filled}</strong>
          </span>
          <span>
            <em>Source</em>
            <strong>{selectedBookmaker ? "Feed" : feedState?.hasOdds ? "Pick feed" : "Manual"}</strong>
          </span>
          <span>
            <em>Errors</em>
            <strong
              className={validationErrorCount > 0 ? "oc-odds-stat-bad tabular-nums" : "tabular-nums"}
            >
              {validationErrorCount}
            </strong>
          </span>
        </div>
      </header>

      <div className="bd-desk-body">
      {!partnershipReady ? (
        <div className="fd-alert" role="status">
          <AlertCircle size={15} aria-hidden />
          <p>All partners must accept before you can save odds for this fixture.</p>
        </div>
      ) : null}

      {toast ? (
        <div
          className={`bd-toast bd-toast-${
            toast.type === "error" ? "error" : "success"
          }`}
          role="status"
        >
          {toast.message}
        </div>
      ) : null}

      {feedsLoading ? (
        <div className="bd-loading oc-odds-loading">
          <Loader2 size={22} className="animate-spin" />
          <span>Loading odds feeds…</span>
        </div>
      ) : null}

      {feedsError ? (
        <div className="fd-alert fd-alert-error" role="alert">
          <AlertCircle size={15} aria-hidden />
          <p>{String(feedsError)}</p>
        </div>
      ) : null}

      {feedState?.hasOdds && bookmakers.length > 0 ? (
        <section className="oc-odds-feeds" aria-label="Autofill sources">
          <header className="oc-odds-feeds-head">
            <span>Autofill source</span>
            <em>
              {selectedBookmaker
                ? `${selectedBookmaker.displayName} selected`
                : `${bookmakers.length} available · tap to select`}
            </em>
          </header>
          <div className="oc-odds-feeds-row">
            {bookmakers.map((item) => {
              const selected = selectedBookmaker?.id === item.id;
              return (
                <button
                  key={String(item.id)}
                  type="button"
                  className={`oc-odds-feed-chip ${selected ? "oc-odds-feed-chip-active" : ""}`}
                  onClick={() => {
                    setSelectedBookmaker(item);
                    setToast({
                      type: "success",
                      message: `${item.displayName} ready for autofill.`,
                    });
                  }}
                >
                  {item.logo ? (
                    <img src={item.logo} alt="" className="oc-odds-feed-logo" />
                  ) : (
                    <span className="oc-odds-feed-logo-fallback" aria-hidden>
                      {(item.displayName || "?")[0]}
                    </span>
                  )}
                  <span>{item.displayName}</span>
                  {selected ? <CheckCircle2 size={13} aria-hidden /> : null}
                </button>
              );
            })}
          </div>
        </section>
      ) : null}

      {!feedsLoading && !marketSections.length ? (
        <div className="bd-empty-state oc-odds-empty">
          <strong>No odds markets loaded</strong>
          <p>
            This fixture has no odds structure yet. Go back to fixtures and try refreshing, or add
            the fixture again if the odds record is missing.
          </p>
          <Link to={fixturesPath} className="oc-odds-empty-link">
            Return to board lanes
          </Link>
        </div>
      ) : (
        <div className="oc-odds-board">
          {marketSections.map(([sectionTitle, sectionData]) => (
            <OddsMarketSection
              key={sectionTitle}
              sectionTitle={sectionTitle}
              sectionData={sectionData}
              validationErrors={validationErrors}
              autofilledMeta={autofilledSections[sectionTitle]}
              canAutofill={Boolean(selectedBookmaker && feedState?.hasOdds)}
              autofillLoading={autofillLoading}
              onAutofill={handleAutofill}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          ))}
        </div>
      )}

      {saveError ? (
        <div className="fd-alert fd-alert-error oc-odds-save-error" role="alert">
          <AlertCircle size={15} aria-hidden />
          <p>{String(saveError)}</p>
        </div>
      ) : null}
      </div>
      </div>
    </OperatorConsoleShell>
  );
};

export default CustomizeOddsDesk;
